/**
 * PlaylistContextMenu Component
 * Context menu (right-click) for playlist items with actions like delete and copy URL
 */

import { useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Trash2, Link, ExternalLink } from 'lucide-react';

interface PlaylistContextMenuProps {
  x: number;
  y: number;
  playlistId: string;
  playlistName: string;
  playlistUri: string;
  onClose: () => void;
  onDelete: (playlistId: string) => void;
}

export function PlaylistContextMenu({
  x,
  y,
  playlistId,
  playlistName,
  playlistUri,
  onClose,
  onDelete,
}: PlaylistContextMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);

  // Calculate adjusted position to keep menu on screen
  const getAdjustedPosition = () => {
    // Use approximate menu dimensions (will be adjusted by CSS max-width if needed)
    const menuWidth = 180;
    const menuHeight = 200; // Approximate height
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const padding = 8;

    let adjustedX = x;
    let adjustedY = y;

    // Adjust horizontal position
    if (x + menuWidth > viewportWidth - padding) {
      adjustedX = viewportWidth - menuWidth - padding;
    }

    // Adjust vertical position
    if (y + menuHeight > viewportHeight - padding) {
      adjustedY = viewportHeight - menuHeight - padding;
    }

    // Ensure menu doesn't go off the left or top edges
    adjustedX = Math.max(padding, adjustedX);
    adjustedY = Math.max(padding, adjustedY);

    return { x: adjustedX, y: adjustedY };
  };

  const position = getAdjustedPosition();

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    // Add slight delay to prevent immediate close from the same click that opened the menu
    setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }, 0);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  const handleCopyUrl = useCallback(() => {
    // Copy Spotify URI to clipboard
    navigator.clipboard.writeText(playlistUri).then(() => {
      console.log('Copied URI to clipboard:', playlistUri);
      // Could show a toast notification here
    }).catch((err) => {
      console.error('Failed to copy URI:', err);
    });
    onClose();
  }, [playlistUri, onClose]);

  const handleCopySpotifyUrl = useCallback(() => {
    // Extract playlist ID from URI and create web URL
    const id = playlistUri.split(':').pop();
    const webUrl = `https://open.spotify.com/playlist/${id}`;

    navigator.clipboard.writeText(webUrl).then(() => {
      console.log('Copied Spotify URL to clipboard:', webUrl);
      // Could show a toast notification here
    }).catch((err) => {
      console.error('Failed to copy URL:', err);
    });
    onClose();
  }, [playlistUri, onClose]);

  const handleDelete = useCallback(() => {
    const confirmed = window.confirm(
      `Are you sure you want to delete "${playlistName}"?\n\nThis action cannot be undone. The playlist will be permanently removed from your Spotify account.`
    );

    if (confirmed) {
      onDelete(playlistId);
    }
    onClose();
  }, [playlistId, playlistName, onDelete, onClose]);

  const menu = (
    <div
      ref={menuRef}
      className="fixed z-[9999] min-w-[180px] bg-zinc-800 border border-zinc-700 rounded-lg shadow-xl overflow-hidden"
      style={{ left: `${position.x}px`, top: `${position.y}px` }}
    >
      <div className="py-1">
        {/* Copy Spotify URI */}
        <button
          onClick={handleCopyUrl}
          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-zinc-200 hover:bg-zinc-700 transition-colors"
        >
          <Link className="w-4 h-4 text-zinc-400" />
          <span>Copy URI</span>
        </button>

        {/* Copy Spotify Web URL */}
        <button
          onClick={handleCopySpotifyUrl}
          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-zinc-200 hover:bg-zinc-700 transition-colors"
        >
          <ExternalLink className="w-4 h-4 text-zinc-400" />
          <span>Copy Spotify URL</span>
        </button>

        {/* Divider */}
        <div className="my-1 h-px bg-zinc-700" />

        {/* Delete */}
        <button
          onClick={handleDelete}
          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-400 hover:bg-red-500/10 hover:text-red-300 transition-colors"
        >
          <Trash2 className="w-4 h-4" />
          <span>Delete Playlist</span>
        </button>
      </div>
    </div>
  );

  // Render menu in a portal to avoid z-index and positioning issues with virtualized list
  return createPortal(menu, document.body);
}
